import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export const HOTEL_CXL_DATE_FORMAT_TRACE = "[HOTEL_CXL_DATE_FORMAT_TRACE]";

function isDevTraceEnabled() {
  try {
    return typeof import.meta !== "undefined" && Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

function emitHotelCxlDateTrace(payload) {
  if (!isDevTraceEnabled()) return;
  try {
    console.debug(HOTEL_CXL_DATE_FORMAT_TRACE, payload);
  } catch {
    /* ignore */
  }
}

/**
 * Parse hotel cancellation deadline for Israel display (DD/MM/YYYY).
 * Does not use loose `dayjs(string)` / `new Date(string)` on slash dates.
 *
 * @param {unknown} raw
 * @param {{ explicitFormat?: "MM/DD/YYYY"|"DD/MM/YYYY"|null }} [options]
 * @returns {{ parsed: import("dayjs").Dayjs|null, detectedFormat: string|null }}
 */
export function parseHotelCancellationDate(raw, options = {}) {
  const explicitFormat = options.explicitFormat ?? null;

  if (raw == null || raw === "") {
    return { parsed: null, detectedFormat: null };
  }

  if (typeof raw === "number" && Number.isFinite(raw)) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    const d = dayjs(ms);
    return d.isValid()
      ? { parsed: d, detectedFormat: "epoch_ms" }
      : { parsed: null, detectedFormat: null };
  }

  if (raw instanceof Date) {
    const d = dayjs(raw);
    return d.isValid()
      ? { parsed: d, detectedFormat: "Date_object" }
      : { parsed: null, detectedFormat: null };
  }

  const s = String(raw).trim();
  if (!s) return { parsed: null, detectedFormat: null };
  if (/^(none|null|undefined|nan|-)$/i.test(s)) {
    return { parsed: null, detectedFormat: "SENTINEL" };
  }

  const dotnet = s.match(/^\/Date\((-?\d+)(?:[+-]\d{4})?\)\/$/);
  if (dotnet) {
    const d = dayjs(Number(dotnet[1]));
    return d.isValid()
      ? { parsed: d, detectedFormat: "dotnet_ticks" }
      : { parsed: null, detectedFormat: null };
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = dayjs(s, "YYYY-MM-DD", true);
    return d.isValid()
      ? { parsed: d, detectedFormat: "ISO_YYYY-MM-DD" }
      : { parsed: null, detectedFormat: "ISO_YYYY-MM-DD_INVALID" };
  }

  if (/^\d{4}-\d{2}-\d{2}[T\s]/.test(s)) {
    const datePart = s.slice(0, 10);
    const d = dayjs(datePart, "YYYY-MM-DD", true);
    return d.isValid()
      ? { parsed: d, detectedFormat: "ISO_DATETIME" }
      : { parsed: null, detectedFormat: "ISO_DATETIME_INVALID" };
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    if (explicitFormat === "MM/DD/YYYY") {
      const d = dayjs(s, "MM/DD/YYYY", true);
      return d.isValid()
        ? { parsed: d, detectedFormat: "MM/DD/YYYY_EXPLICIT" }
        : { parsed: null, detectedFormat: "MM/DD/YYYY_EXPLICIT_INVALID" };
    }
    const d = dayjs(s, "DD/MM/YYYY", true);
    return d.isValid()
      ? { parsed: d, detectedFormat: "DD/MM/YYYY" }
      : { parsed: null, detectedFormat: "DD/MM/YYYY_INVALID" };
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
    const d = dayjs(s, "DD-MM-YYYY", true);
    return d.isValid()
      ? { parsed: d, detectedFormat: "DD-MM-YYYY" }
      : { parsed: null, detectedFormat: "DD-MM-YYYY_INVALID" };
  }

  return { parsed: null, detectedFormat: "UNPARSED" };
}

/**
 * Format hotel cancellation deadline for customer-facing UI (always DD/MM/YYYY).
 *
 * @param {unknown} raw
 * @param {{ sourcePath?: string, explicitFormat?: "MM/DD/YYYY"|"DD/MM/YYYY"|null, trace?: boolean }} [options]
 * @returns {string|null}
 */
export function formatHotelCancellationDate(raw, options = {}) {
  const sourcePath = options.sourcePath ?? "unknown";
  const explicitFormat =
    options.explicitFormat ??
    options.supplierDateFormat ??
    options.dateFormat ??
    null;
  const shouldTrace = options.trace !== false;

  const { parsed, detectedFormat } = parseHotelCancellationDate(raw, {
    explicitFormat,
  });

  const displayedDate = parsed?.isValid() ? parsed.format("DD/MM/YYYY") : null;

  if (shouldTrace) {
    emitHotelCxlDateTrace({
      rawCancellationDate: raw,
      parsedDate: parsed?.isValid() ? parsed.format("YYYY-MM-DD") : null,
      detectedFormat,
      displayedDate,
      sourcePath,
    });
  }

  return displayedDate;
}
